<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <section ref="campaignRef" :class="containerStyles">
    <div :class="textWrapperStyles">
      <p v-if="subtitle" :class="subheaderStyles">
        {{ subtitle }}
      </p>
      <header2 :text="title" classes="mb-5" :isUmbraco="isUmbraco" :theme="themeType" />
      <rich-text v-if="text" :text="text" :isUmbraco="isUmbraco" :theme="themeType" />
      <link-button
        v-if="ctaLink"
        :label="ctaLabel"
        :url="ctaLink"
        :target="target"
        :is-umbraco="isUmbraco"
        classes="mb-8 mt-8 lg:mb-0 lg:mb-[60px] self-start"
        :theme="themeType"
      />
    </div>
    <div v-if="video" :class="imgWrapperStyles">
      <vimeo-player v-if="allowVideo" :videoId="video" :portrait="true" />
      <div v-else>
        <div
          class="aspect-video bg-orange flex justify-center items-center text-white text-xxl p-5"
        >
          <button @click="showCookieConsentDialog" class="btn btn-primary mt-4">
            {{ setCookie }}
          </button>
        </div>
      </div>
    </div>
    <div v-if="image" :class="imgWrapperStyles">
      <curtain
        :show="revealAnimation"
        :pos="imgPos"
        :isUmbraco="isUmbraco"
        :theme="theme"
      />

      <Transition
        enter-active-class="transition-transform duration-700"
        leave-active-class="transition duration-300"
        :enter-from-class="imgReavelAnim"
        :leave-to-class="imgReavelAnim"
      >
        <div v-if="revealAnimation" class="w-full h-full relative">
          <fixed-stroke v-if="stroke" :stroke="stroke" :pos="imgPos" />
          <image-component
            :image="image"
            :img-alt="imgAlt"
            classes="w-full h-full object-cover"
          />
        </div>
      </Transition>
    </div>
  </section>
</template>

<script lang="ts" setup>
interface Window {
  Cookiebot?: {
    renew: () => void;
  };
}
import RichText from "@components/Typography/RichText.ce.vue";
import Header2 from "@components/Typography/Header2.ce.vue";
import LinkButton from "@atoms/LinkButton/LinkButton.ce.vue";
import VimeoPlayer from "@molecules/VimeoPlayer/VimeoPlayer.ce.vue";
import FixedStroke from "@/components/Atoms/Image/FixedStroke.vue";
import ImageComponent from "@atoms/Image/Img.vue";
import Curtain from "@atoms/Image/Curtain.vue";
import { changeTheme, getThemeType, getCookie } from "@/util/helper";
import { onIntersect } from "@/composables/onIntersect";
import { getTranslations } from "@/util/api";
import {
  computed,
  toRefs,
  onMounted,
  onUnmounted,
  ref,
  Ref,
  watch,
  onBeforeMount,
} from "vue";
import { ICampaignProps } from "@/types/components/ICampaignProps";

const props = withDefaults(defineProps<ICampaignProps>(), {
  imgPos: "right",
  isUmbraco: false,
});

const {
  theme,
  title,
  subtitle,
  text,
  ctaLabel,
  ctaLink,
  target,
  imgPos,
  stroke,
  image,
  videoId,
  isUmbraco,
} = toRefs(props);

const observer: Ref<IntersectionObserver | null> = ref(null);
const campaignRef = ref(null);
const revealAnimation = ref(false);
const setCookie = ref<string | null>();
const onEnter = () => {
  changeTheme(theme.value);
  revealAnimation.value = true;
};
const onExit = () => {};

const fetchInitialData = async () => {
  [setCookie.value] = await Promise.all([getTranslations("VideoCookie")]);
};

onBeforeMount(fetchInitialData);

onMounted(() => {
  observer.value = onIntersect(campaignRef.value, onEnter, onExit, false, {
    threshold: 0.6,
  });
});

onUnmounted(() => {
  if (!observer.value) return;
  observer.value.disconnect();
});

watch(theme, (value) => {
  if (!value) return;
  changeTheme(value);
});

const textWrapperStyles = computed(() => {
  const colStart = imgPos.value === "right" ? `lg:col-start-2` : `lg:col-start-3`;
  const marginLG = imgPos.value === "right" ? `lg:mx-12 2xl:mx-20` : `lg:mx-auto`;

  return `relative z-30 flex flex-col justify-center
          col-start-2 col-span-12
          mt-[80px] mb-[60px]
          lg:col-span-1 lg:row-start-1 ${colStart}
          lg:my-0 lg:max-w-[75%] ${marginLG}
          xl:max-w-[50%]`;
});

const themeType = computed(() => {
  return getThemeType(theme.value);
});

const video = computed(() => {
  if (!videoId) return;
  return Number(videoId?.value);
});

const containerStyles = computed(() => {
  const bgColor = isUmbraco.value ? `bg-${theme.value}` : ``;
  return `${bgColor} twoColSectionContainer`;
});

const imgWrapperStyles = computed(() => {
  const colStart = imgPos.value === "right" ? `lg:col-start-3` : `lg:col-start-2`;
  return `relative overflow-hidden
          flex items-center 
          col-span-full ${colStart}
          lg:h-screen lg:col-span-1 lg:row-start-1 lg:col-span-1`;
});

const imgReavelAnim = computed(() => {
  if (!stroke?.value) return "";
  return imgPos.value === "right" ? `translate-x-1/4` : `-translate-x-1/4`;
});

const subheaderStyles = computed(() => {
  const color =
    props.isUmbraco && themeType.value === "dark"
      ? "text-white"
      : props.isUmbraco && themeType.value === "light"
      ? "text-aubergine"
      : "text-typography";
  return `${color} text-xs md:text-sm font-semibold mb-10 themeTransition`;
});

const allowVideo = computed(() => {
  const cookieString = getCookie("CookieConsent");
  // COOKIE STRING FOR LOCAL TESTING
  //const cookieString = `{stamp:'JiFAD24X96gRckejqUkd9Etot7hUEJ4nGYuJYimcXtovtxwhziwrog==',necessary:true,preferences:true,statistics:true,marketing:true,method:'explicit',ver:2,utc:1712579039412,region:'dk'}`;
  if (!cookieString) return null;
  const marketingMatch = cookieString.match(/marketing:\s*(true|false)/);

  if (marketingMatch) {
    // Extract the matched value and parse it to a boolean
    const marketingValue = JSON.parse(marketingMatch[1]);
    console.log("marketingValue: ", marketingValue);

    return marketingValue;
  } else {
    console.error("Marketing property not found in cookie string");
    return null;
  }
});
const showCookieConsentDialog = () => {
  if (window.Cookiebot) {
    window.Cookiebot.renew();
  } else {
    console.error("Cookiebot is not available");
  }
};
</script>
